import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { CreditCard, Mountain, Compass, Users, Sparkles } from "lucide-react";

const tierConfig = {
  scout: {
    name: "Scout",
    icon: Compass,
    description: "Free tier with basic features",
    color: "text-muted-foreground",
  },
  adventurer: {
    name: "Adventurer",
    icon: Mountain,
    description: "Full access to community features",
    color: "text-primary",
  },
  organizer: {
    name: "Organizer",
    icon: Users,
    description: "Event hosting and route creation",
    color: "text-orange-500",
  },
};

const SubscriptionCard = () => {
  const { tier, isBetaUser } = useSubscription();
  const navigate = useNavigate();

  // Beta users with adventurer access should show adventurer config
  const effectiveTier = isBetaUser && tier === 'scout' ? 'adventurer' : tier;
  const config = tierConfig[effectiveTier];
  const TierIcon = config.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription
        </CardTitle>
        <CardDescription>Your current plan</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Plan */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full bg-background ${config.color}`}>
              <TierIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{config.name}</span>
                {isBetaUser ? (
                  <Badge className="bg-primary/10 text-primary border-primary/20">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Beta
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    Free
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{config.description}</p>
            </div>
          </div>
        </div>

        {/* Beta Status Message */}
        {isBetaUser && (
          <div className="text-sm text-center p-3 bg-primary/5 rounded-lg border border-primary/10">
            <p className="text-muted-foreground">
              <Sparkles className="w-4 h-4 inline mr-1 text-primary" />
              You have free beta access to all Adventurer features. 
              We'll notify you before any pricing changes.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          {!isBetaUser && tier === 'scout' && (
            <Button 
              variant="hero" 
              onClick={() => navigate('/pricing')}
              className="w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Get Free Beta Access
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/pricing')}
            className="text-muted-foreground"
          >
            View all plans
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard;
